import type { IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { AnalyticsFilter, InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import type { IssueSeverity } from './PolicyIssues';

/** Turns scanned for the sensitivity-flag rollup (the tile and the Issues tab share this). */
export const SENSITIVITY_SCAN_LIMIT = 500;

/** One sensitivity-flag category (e.g. PII) with how many turns carried it in the window. */
export interface SensitivityAlert {
    /** Raw flag key from the classifier, e.g. `pii`, `data_exposure`, `credentials`. */
    flag: string;
    /** Readable label, e.g. "Pii", "Data exposure". */
    label: string;
    count: number;
    severity: IssueSeverity;
}

/** Turn a sensitivity-flag key (e.g. "pii", "data_exposure") into a readable label. */
export function humanizeFlag(flag: string): string {
    const spaced = flag.replace(/[_-]+/g, ' ').trim();
    return spaced.length === 0 ? flag : spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Severity of a sensitivity-flag category from its turn volume. */
export function flagSeverity(count: number): IssueSeverity {
    if (count >= 20) return 'critical';
    if (count >= 5) return 'high';
    return 'medium';
}

/** Count sensitivity flags across a set of flagged turns, worst (most turns) first. */
export function countSensitivityFlags(records: InteractionRecord[]): SensitivityAlert[] {
    const counts = new Map<string, number>();
    for (const record of records) {
        for (const flag of record.sensitivityFlags) counts.set(flag, (counts.get(flag) ?? 0) + 1);
    }
    return [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([flag, count]) => ({ flag, label: humanizeFlag(flag), count, severity: flagSeverity(count) }));
}

/**
 * Load the sensitivity-flag rollup — one entry per flag category (PII, credentials, data
 * exposure) with its turn count — from the interactions OLAP, over the active date range.
 * Shared by the Analytics "Sensitive Data Alerts" tile and the register posture so both agree.
 */
export async function loadSensitivityAlerts(
    gateway: IAnalyticsGateway,
    opts: { filters?: AnalyticsFilter[]; limit?: number } = {},
): Promise<{ alerts: SensitivityAlert[]; totalMatched: number }> {
    const res = await gateway.records({
        source: 'interactions',
        hasSensitivityFlag: true,
        filters: opts.filters,
        orderBy: { column: 'timestamp', direction: 'desc' },
        limit: opts.limit ?? SENSITIVITY_SCAN_LIMIT,
    });
    return { alerts: countSensitivityFlags(res.records), totalMatched: res.totalMatched };
}
